import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Trophy, Flame, Share2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Shareable result card surfaced after every game submission. Shows the
 * score breakdown plus the user's running streak and total. Provides a
 * single Share button that uses the Web Share API where available and
 * falls back to clipboard-copy with a toast.
 */
const GameResultCard = ({
  language = 'en',
  gameTitle,
  correct,
  total,
  score,
  perfect,
  streak,
  newTotal,
  subtitle,
}) => {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);

  const buildShareText = () => {
    const heart = perfect ? '🏆' : score > 0 ? '✨' : '📖';
    const dayWord = streak === 1 ? t('games.result.shareDayOne') : t('games.result.shareDayMany');
    return t('games.result.shareText', {
      heart, title: gameTitle, correct, total, score, streak, dayWord,
    });
  };

  const onShare = async () => {
    const text = buildShareText();
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (navigator.share) {
      try {
        await navigator.share({ title: gameTitle, text, url });
        return;
      } catch {
        /* user cancelled — fall through */
      }
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      setCopied(true);
      toast.success(t('games.result.copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('games.result.couldNotShare'));
    }
  };

  return (
    <Card
      className="bg-gradient-to-br from-purple-600 to-pink-600 text-white border-0 shadow-xl"
      data-testid="game-result-card"
    >
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest opacity-80">{gameTitle}</p>
            {subtitle && <p className="text-xs opacity-80 italic">{subtitle}</p>}
          </div>
          {perfect && (
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-300 text-purple-900 text-[10px] font-bold uppercase tracking-wide">
              <Trophy className="w-3 h-3" />
              {t('games.result.perfect')}
            </div>
          )}
        </div>

        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-bold tabular-nums" data-testid="game-result-score">
            +{score}
          </span>
          <span className="text-sm opacity-80">
            {t('games.result.pts')}
          </span>
        </div>

        <p className="text-sm opacity-90">
          {t('games.result.correctLabel')}: <span className="font-semibold">{correct}/{total}</span>
        </p>

        <div className="flex items-center justify-between text-xs pt-2 border-t border-white/20">
          <div className="flex items-center gap-1.5" data-testid="game-result-streak">
            <Flame className="w-3.5 h-3.5 text-orange-300" />
            <span>
              {streak} {streak === 1 ? t('games.result.streakDay') : t('games.result.streakDays')}
            </span>
          </div>
          <div className="opacity-90">
            {t('games.result.totalLabel')}:{' '}
            <span className="font-semibold tabular-nums">{newTotal}</span>
          </div>
        </div>

        <Button
          onClick={onShare}
          className="w-full bg-white text-purple-700 hover:bg-white/90 font-semibold"
          data-testid="game-result-share-btn"
        >
          {copied
            ? <Check className="w-4 h-4 mr-2" />
            : <Share2 className="w-4 h-4 mr-2" />}
          {t('games.result.share')}
        </Button>
      </CardContent>
    </Card>
  );
};

export default GameResultCard;
